interface SpotifyConfig {
  clientId: string;
  redirectUri: string;
}

interface SpotifyToken {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
}

export class SpotifyAuth {
  private config: SpotifyConfig;
  private token: string | null = null;
  private codeVerifier: string | null = null;
  private onUnauthorized?: () => void;

  constructor(config: SpotifyConfig) {
    this.config = config;
  }

  /**
   * Set callback for 401 Unauthorized errors
   */
  setOnUnauthorized(callback: () => void): void {
    this.onUnauthorized = callback;
  }

  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  }

  private generateRandomString(length: number): string {
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const values = crypto.getRandomValues(new Uint8Array(length));
    return values.reduce((acc, x) => acc + possible[x % possible.length], '');
  }

  async getAuthUrl(): Promise<string> {
    const scopes = [
      'user-read-playback-state',
      'user-modify-playback-state',
      'user-read-currently-playing',
      'user-read-playback-position',
      'streaming',
      'user-read-email',
      'user-read-private',
    ];

    this.codeVerifier = this.generateRandomString(64);
    localStorage.setItem('code_verifier', this.codeVerifier);
    
    const codeChallenge = await this.generateCodeChallenge(this.codeVerifier);

    const params = new URLSearchParams({
      client_id: this.config.clientId,
      response_type: 'code',
      redirect_uri: this.config.redirectUri,
      scope: scopes.join(' '),
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
      show_dialog: 'true',
    });

    return `https://accounts.spotify.com/authorize?${params.toString()}`;
  }

  async handleCallback(code: string): Promise<void> {
    const codeVerifier = localStorage.getItem('code_verifier');
    if (!codeVerifier) {
      throw new Error('Code verifier not found');
    }

    const params = new URLSearchParams({
      client_id: this.config.clientId,
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: this.config.redirectUri,
      code_verifier: codeVerifier,
    });

    const response = await fetch('https://accounts.spotify.com/api/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      throw new Error('Failed to exchange code for token');
    }

    const data: SpotifyToken = await response.json();
    this.token = data.access_token;
    localStorage.setItem('spotify_token', data.access_token);
    localStorage.removeItem('code_verifier');
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('spotify_token');
    }
    return this.token;
  }

  clearToken(): void {
    this.token = null;
    localStorage.removeItem('spotify_token');
  }

  async getCurrentTrack(): Promise<any> {
    const token = this.getToken();
    if (!token) return null;

    try {
      const response = await fetch('https://api.spotify.com/v1/me/player/currently-playing', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 204) return null;
      if (!response.ok) {
        if (response.status === 401) {
          console.warn('🔒 Spotify API returned 401 - Token expired');
          this.clearToken();
          if (this.onUnauthorized) {
            this.onUnauthorized();
          }
        }
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching current track:', error);
      return null;
    }
  }

  async getAnalysis(_trackId: string): Promise<any> {
    // Audio Features API requires special permissions we don't have
    // Use simulated analysis based on standard music metrics
    return this.generateSimulatedAnalysis();
  }


  private generateSimulatedAnalysis(): any {
    // Fallback: generate generic analysis data
    const tempo = 120;
    const duration = 180; // 3 minutes
    const beatsPerSecond = tempo / 60;
    const beatDuration = 1 / beatsPerSecond;
    
    const beats = [];
    for (let time = 0; time < duration; time += beatDuration) {
      beats.push({ start: time, duration: beatDuration, confidence: 0.5 });
    }
    
    const bars = [];
    for (let i = 0; i < beats.length; i += 4) {
      bars.push({ start: beats[i].start, duration: beatDuration * 4, confidence: 0.5 });
    }
    
    return {
      beats,
      bars,
      sections: [{ start: 0, duration, loudness: -5, tempo, key: 0, mode: 1 }],
      segments: [],
      track: {
        tempo,
        key: 0,
        mode: 1,
        time_signature: 4,
        duration,
        loudness: -5,
        energy: 0.7,
        danceability: 0.7,
        valence: 0.5,
      },
    };
  }
}
